import axios, { CancelTokenSource } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { BaseApi } from '../api/api.g';
import { ApiConfig } from '../api/BaseApi';
import { useAppDispatch, useAppSelector } from './store-hook';
import authSlice, { FirebaseReference } from '../store/authSlice';
import { getApiErrorStatus, LOCKED_SESSION_STATUS } from '../helpers/apiErrorHelper';
import { rootNavigate } from '../navigation/rootNavigation';
import { requestSurveyWebViewAuthToken } from '../helpers/surveyWebViewAuth';
import {
  FIREBASE_AUTH_RETRY_NOT_ATTEMPTED,
  getFirebaseAccessToken,
  retryFirebaseAuthenticatedRequest,
} from '../helpers/firebaseAuthRetry';

const useApi = <T extends BaseApi>(api: new (config: ApiConfig) => T): T => {
  const authStore = useAppSelector((selector) => selector.auth);
  const navStore = useAppSelector((selector) => selector.navBar);
  const uiLanguage = useAppSelector((selector) => selector.userPrefs.uiLanguage);
  const dispatch = useAppDispatch();
  const apiInstance: T = useMemo<T>(() => new api({ accessToken: () => Promise.resolve('') }), [api]);
  const [isCancel, setIsCancel] = useState(false);
  const [tokenSource, setTokenSource] = useState<CancelTokenSource | undefined>(undefined);

  const [customTokenValue, setCustomTokenValue] = useState<string>();

  apiInstance.tokenSource = tokenSource;

  useEffect(() => {
    apiInstance.setCustomCulture(uiLanguage || process.env.REACT_APP_DEFAULT_LANG || 'en');
  }, [apiInstance, uiLanguage]);

  // Flag that the token was cancelled, so it can be used to renew the cancellation token
  apiInstance.onCancel = () => {
    apiInstance.isCancelled = true;
    setIsCancel(true);
  };

  useEffect(() => {
    apiInstance.config.accessToken = () => getFirebaseAccessToken({
      isWebView: navStore.isWebView,
      fallbackToken: authStore.idToken,
      requestWebViewToken: requestSurveyWebViewAuthToken,
      getFirebaseToken: async (forceRefresh) => FirebaseReference.currentUser?.getIdToken(forceRefresh),
    });
  }, [apiInstance, authStore.idToken, navStore.isWebView]);

  useEffect(() => {
    const axiosInstance = (apiInstance as any).instance;
    const responseInterceptors = axiosInstance?.interceptors?.response;

    if (!responseInterceptors) {
      return;
    }

    const interceptorId = responseInterceptors.use(
      (response) => response,
      async (error) => {
        const status = getApiErrorStatus(error);
        const requestConfig = error?.config;
        const retryResult = await retryFirebaseAuthenticatedRequest({
          status,
          customToken: apiInstance.config.customToken,
          requestConfig,
          isWebView: navStore.isWebView,
          fallbackToken: authStore.idToken,
          requestWebViewToken: requestSurveyWebViewAuthToken,
          getFirebaseToken: async (forceRefresh) => FirebaseReference.currentUser?.getIdToken(forceRefresh),
          retryRequest: (config) => axiosInstance.request(config),
        });

        if (retryResult !== FIREBASE_AUTH_RETRY_NOT_ATTEMPTED) {
          return retryResult;
        }

        if (status === LOCKED_SESSION_STATUS) {
          dispatch(authSlice.actions.logout());
          rootNavigate('login', { newLogin: 'true' });
        }

        return Promise.reject(error);
      },
    );

    return () => responseInterceptors.eject(interceptorId);
  }, [apiInstance, authStore.idToken, navStore.isWebView, dispatch]);

  useEffect(() => {
    setTokenSource(axios.CancelToken.source());
  }, [api]);

  useEffect(() => {
    setIsCancel(false);
    apiInstance.isCancelled = false;
  }, [tokenSource]);

  // Returning the isCancel to false
  useEffect(() => {
    if (isCancel) {
      const newCancellationToken = axios.CancelToken.source();
      setTokenSource(newCancellationToken);
      apiInstance.tokenSource = newCancellationToken;
    }
  }, [isCancel, apiInstance]);

  return apiInstance;
};

export default useApi;
